import imgEmptyNoData from 'assets/images/img_emptyview_no_data.png';
import KTLabel, { KTLabelSize } from 'general/components/OtherKeenComponents/KTLabel';
import KTPagination, {
    KTPaginationColor,
} from 'general/components/OtherKeenComponents/KTPagination';
import KTTable from 'general/components/OtherKeenComponents/KTTable';
import EmptyView from 'general/components/Others/EmptyView';
import useRouter from 'hooks/useRouter';
import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import AppRoute from 'general/constants/AppRoute';
import tableStyle from 'general/components/OtherKeenComponents/KTTable/tableStyle';
import useConfigTableColumn from 'hooks/useConfigTableColumn';
import Utils from 'general/utils/Utils';
import UIHelper from 'general/helpers/UIHelper';
import ChargeHelper from 'features/Charge/chargeHelper';
import PropTypes from 'prop-types';
import MenuActionDropdown from 'general/components/Others/MenuActionDropdown';
import AppColors from 'general/constants/AppColors';
import { AppCustomDialogHelper } from 'general/components/AppDialogCustom';

StationEnergyView.propTypes = {
    agencyId: PropTypes.string,
    primaryRole: PropTypes.string,
    stationEnergyList: PropTypes.array,
    stationEnergyTotal: PropTypes.number,
    onFilterChanged: PropTypes.func,
    filter: PropTypes.object,
    reconciliationDetail: PropTypes.object,
};

const defaultColumnIds = ['STATION_CODE', 'STATION_NAME', 'STATUS', 'ENERGY', 'DIFFERENCE', 'AMOUNT_BEFORE_TAX', 'AMOUNT_AFTER_TAX', 'ENERGY_PAYER'];

function StationEnergyView(props) {
    // ---- params ----
    const router = useRouter();
    const { t } = useTranslation();
    const { agencyId, primaryRole, stationEnergyList, stationEnergyTotal, onFilterChanged, filter, reconciliationDetail } = props;
    const { user } = useSelector((state) => state.auth);
    const fullColumns = useMemo(() => {
        return [
            {
                id: 'STATION_CODE',
                name: UIHelper.buildTableColumn('Mã trạm sạc'),
                selector: (row) => {
                    console.log('row: ', row);
                    return primaryRole === 'INVESTOR' ? row?.station?.stationCode : row?.stationCode ?? row?.stationId
                },
                width: '140px',
                style: tableStyle.columns.normal,
                sortable: true,
            },
            {
                id: 'STATION_NAME',
                name: UIHelper.buildTableColumn('Tên trạm sạc'),
                selector: (row) => (
                    <span
                        className="cursor-pointer hover-opacity-60"
                        onClick={() => {
                            Utils.openInNewTab(`${AppRoute.stationList}/${primaryRole === 'INVESTOR' ? row?.station?.stationId : row?.stationId}`);
                        }}
                    >
                        {primaryRole === 'INVESTOR' ? row?.station?.name : row?.name}
                    </span>
                ),
                minWidth: '230px',
                style: tableStyle.columns.link,
            },
            {
                id: 'STATUS',
                name: UIHelper.buildTableColumn('Trạng thái hoạt động'),
                minWidth: '150px',
                selector: (row) => (
                    <KTLabel
                        text={ChargeHelper.Utils.getChargeStationStatusName(primaryRole === 'INVESTOR' ? row?.station?.status : row?.status)}
                        isInline={true}
                        size={KTLabelSize.medium}
                        additionalClassName={`label-light-${ChargeHelper.Utils.getChargeStationStatusColor(
                            primaryRole === 'INVESTOR' ? row?.station?.status : row?.status
                        )}`}
                    />
                ),
            },
            {
                id: 'ENERGY',
                name: UIHelper.buildTableColumn('Sản lượng điện tiêu thụ', 'kWh'),
                selector: (row) => Utils.formatNumber(row?.totalEnergy / 1000?.toFixed(3) ?? 0),
                style: tableStyle.columns.normal,
                sortable: false,
                minWidth: '200px',
                right: true,
            },
            {
                id: 'DIFFERENCE',
                name: UIHelper.buildTableColumn('Chênh lệch với hóa đơn', '%'),
                selector: (row) => {
                    const A = Number(row?.totalEnergy ?? 0) / 1000;
                    const B = Number(_.sumBy(row?.station?.energyBills ?? [], 'evnEnergyUsed') ?? 0);
                    if (B === 0) return 0;

                    const percent = ((A - B) * 100) / B;
                    return Number(percent.toFixed(3));
                },
                style: tableStyle.columns.normal,
                minWidth: '200px',
                right: true,
            },
            {
                id: 'AMOUNT_BEFORE_TAX',
                name: UIHelper.buildTableColumn('Tiền điện trước thuế', 'VNĐ'),
                selector: (row) => Utils.formatNumber(Math.round(row?.totalEnergyCost) ?? 0),
                style: tableStyle.columns.normal,
                sortable: false,
                minWidth: '200px',
                right: true,
            },
            {
                id: 'AMOUNT_AFTER_TAX',
                name: UIHelper.buildTableColumn('Tiền điện sau thuế', 'VNĐ'),
                selector: (row) => Utils.formatNumber(Math.round(row?.totalEnergyCost * (1 + 0.08)) ?? 0),
                style: tableStyle.columns.normal,
                sortable: false,
                minWidth: '200px',
                right: true,
            },
            {
                id: 'ENERGY_PAYER',
                name: UIHelper.buildTableColumn('Người trả tiền điện'),
                selector: (row) => primaryRole === 'INVESTOR'
                    ? row?.station?.isAgencyPayEnergyCost ? 'NĐT nhượng quyền' : 'Nhà điều hành'
                    : row?.isAgencyPayEnergyCost ? 'Chủ mặt bằng' : 'Nhà điều hành',
                style: tableStyle.columns.normal,
                sortable: false,
                minWidth: '180px',
            },
            {
                name: UIHelper.buildTableColumn('Thao tác'),
                style: tableStyle.columns.normal,
                sortable: false,
                width: '90px',
                style: {
                    position: 'sticky',
                    right: 0,
                    backgroundColor: '#fff',
                    borderLeft: '1px solid #ebedfe',
                },
                cell: (row) => (
                    <MenuActionDropdown
                        menuAction={[
                            {
                                iconElement: (
                                    <i
                                        className="fa-regular fa-eye"
                                        style={{ color: AppColors.gray[700], fontSize: '1.1rem' }}
                                    ></i>
                                ),
                                text: t('Xem chi tiết'),
                                click: () => {
                                    showStationEnergyDialog(row);
                                },
                                show: true,
                            },
                        ]}
                    />
                )
            },
        ];
    }, [t]);

    const prefKey = useMemo(() => {
        const userId = user.userId;
        const pathName = `${router.pathname}${router.location.hash}`;
        const key = `${userId}_${pathName}`;
        return key;
    }, [user, router]);
    const [fullColumnIds, setFullColumnIds] = useState(fullColumns?.map((row) => row?.id).filter(Boolean));
    const {
        columnIds,
        columns,
        onChangeCheckboxValue,
        resetDefaultTableColumn,
        saveChanges,
        onSelectAll,
        onUnSelectAll,
        fullColumnsIdSorted
    } = useConfigTableColumn(prefKey, fullColumnIds, defaultColumnIds, fullColumns);
    // ---- methods ----

    const handleFilterChanged = (filter) => {
        if (onFilterChanged) {
            onFilterChanged(filter);
        }
    };

    const StationEnergyTable = ({ textHeader = [], contents = [], total = 0 }) => {
        return (
            <div>
                <div
                    className="row m-0 position-relative p-4 bg-white rounded-lg"
                >
                    <div className="rounded-lg p-0" style={{ border: "1px solid #EBEDEF" }}>
                        <table className="w-100" style={{ borderCollapse: "collapse" }}>
                            <thead>
                                <tr
                                    className="font-weight-bolder p-3"
                                    style={{
                                        backgroundColor: "#F7F7FD",
                                        fontSize: Utils.returnFontSize(12),
                                        color: AppColors.gray[800],
                                    }}
                                >
                                    {textHeader.map((header, index) => (
                                        <th
                                            key={index}
                                            className={`p-3 ${index === 0 ? "text-left" : "text-right"}`}
                                            style={{ minWidth: index === 0 ? '200px' : '100px' }}
                                        >
                                            {UIHelper.buildTableColumn(header.title, header.unit)}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {Array.isArray(contents) && contents.map((row, rowIndex) => (
                                    <tr
                                        key={rowIndex}
                                        style={{ borderTop: "1px solid #EBEDEF" }}
                                    >
                                        {textHeader.map((_, colIndex) => (
                                            <td
                                                key={colIndex}
                                                className={`p-3 ${colIndex === 0 ? "text-left" : "text-right"}`}
                                            >
                                                {typeof row[Object.keys(row)[colIndex]] === 'number'
                                                    ? Utils.formatNumber(row[Object.keys(row)[colIndex]])
                                                    : row[Object.keys(row)[colIndex]] ?? '--'}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                                <tr style={{ borderTop: "2px solid #EBEDEF", fontWeight: 'bold' }}>
                                    <td className="p-3 text-left">Tổng</td>
                                    {Array.from({ length: textHeader.length - 2 }).map((_, i) => (
                                        <td key={i}></td>
                                    ))}
                                    <td className="p-3 text-right">{total}</td>
                                </tr>

                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Action Buttons */}
                <div
                    className="px-4 py-3 d-flex justify-content-end"
                    style={{ borderTop: `1px solid ${AppColors.gray[200]}` }}
                >
                    {UIHelper.buildNegativeButton(
                        'Đóng',
                        () => AppCustomDialogHelper.hide(),
                    )}
                </div>
            </div>
        );
    };

    const showStationEnergyDialog = (row) => {
        let textHeader = [];
        let contents = [];
        let total = 0;

        switch (row?.station?.inputPricingMode) {
            case 'FLAT':
                textHeader = [
                    { title: 'Chỉ số', unit: '' },
                    { title: 'Giá trị', unit: '' },
                ];
                contents = [
                    {
                        name: 'Điện tiêu thụ',
                        value: Utils.formatNumber(((row?.totalEnergy ?? 0) / 1000).toFixed(3)) + ' kWh'
                    },
                    {
                        name: 'Đơn giá chưa VAT',
                        value: Utils.formatNumber(row?.station?.inputFlatPrice) + ' VNĐ/kWh'
                    },
                ];
                total = Utils.formatNumber(((row?.totalEnergy ?? 0) / 1000) * (row?.station?.inputFlatPrice ?? 0)) + ' VNĐ';
                break;

            case 'HOURS':
                textHeader = [
                    { title: 'Khung giờ', unit: '' },
                    { title: 'Điện tiêu thụ', unit: 'kWh' },
                    { title: 'Đơn giá chưa VAT', unit: 'VNĐ/kWh' },
                    { title: 'Thành tiền', unit: 'VNĐ' },
                ];

                const hourData = [
                    {
                        name: 'Khung giờ bình thường',
                        energyWh: row?.totalNormalHoursEnergy ?? 0,
                        price: row?.station?.inputNormalPrice ?? 0
                    },
                    {
                        name: 'Khung giờ cao điểm',
                        energyWh: row?.totalPeakHoursEnergy ?? 0,
                        price: row?.station?.inputPeakPrice ?? 0
                    },
                    {
                        name: 'Khung giờ thấp điểm',
                        energyWh: row?.totalOffPeakHoursEnergy ?? 0,
                        price: row?.station?.inputOffPeakPrice ?? 0
                    }
                ];

                let totalHoursAmount = 0;

                contents = hourData.map(item => {
                    const energyKwh = (item.energyWh / 1000).toFixed(3);
                    const amount = energyKwh * item.price;
                    totalHoursAmount += amount;

                    return {
                        name: item.name,
                        energy: energyKwh,
                        pricing: Utils.formatNumber(item.price),
                        amount: Utils.formatNumber(amount)
                    };
                });

                total = Utils.formatNumber(totalHoursAmount) + ' VNĐ';
                break;

            case 'TIERED':
                textHeader = [
                    { title: 'Bậc thang', unit: '' },
                    { title: 'Điện tiêu thụ', unit: 'kWh' },
                    { title: 'Đơn giá chưa VAT', unit: 'VNĐ/kWh' },
                    { title: 'Thành tiền', unit: 'VNĐ' },
                ];

                const totalEnergyKwh = (row?.totalEnergy ?? 0) / 1000;
                const tierLimits = [50, 50, 100, 100, 100, Infinity];
                const tierNames = [
                    'Từ 0-50 kWh',
                    'Từ 51-100 kWh',
                    'Từ 101-200 kWh',
                    'Từ 201-300 kWh',
                    'Từ 301-400 kWh',
                    'Từ 401 kWh trở lên'
                ];
                const prices = (row?.station?.inputTieredPrice ?? []).map(p => Number(p?.price ?? 0));

                let remaining = totalEnergyKwh;
                total = 0;

                contents = tierNames.map((name, i) => {
                    const used = remaining > 0 ? Math.min(remaining, tierLimits[i]) : 0;
                    const pricing = prices[i] || 0;
                    const amount = used * pricing;

                    remaining -= used;
                    total += amount;

                    return {
                        name,
                        energy: used,
                        pricing: Utils.formatNumber(pricing),
                        amount: Utils.formatNumber(amount)
                    };
                });

                total = Utils.formatNumber(total);
                break;

            default:
                break;
        }

        AppCustomDialogHelper.show(
            row?.name || 'Chi tiết trạm',
            <StationEnergyTable
                textHeader={textHeader}
                contents={contents}
                total={total}
            />,
            'lg',
            true,
            true
        );
    };

    return (
        <div>
            <div>
                <span style={UIHelper.buildFontStyle('600', '16px', AppColors.gray[800])}>Tổng tiền điện:</span>
                <span style={UIHelper.buildFontStyle('600', '16px', AppColors.primary)}>
                    {' '} {Utils.formatNumber(Math.floor(reconciliationDetail?.metadata?.totalEnergyCostBeforeTax) ?? 0)} VNĐ
                </span>
                <span style={UIHelper.buildFontStyle('600', '16px', AppColors.gray[500])}>{' '}•{' '}</span>
                <span style={UIHelper.buildFontStyle('600', '16px', AppColors.gray[800])}>Thuế GTGT:</span>
                <span style={UIHelper.buildFontStyle('600', '16px', AppColors.primary)}>
                    {' '} {Utils.formatNumber(Math.floor(reconciliationDetail?.metadata?.totalEnergyCostBeforeTax * 0.08) ?? 0)} VNĐ
                </span>
                <span style={UIHelper.buildFontStyle('600', '16px', AppColors.gray[500])}>{' '}•{' '}</span>
                <span style={UIHelper.buildFontStyle('600', '16px', AppColors.gray[800])}>Tổng tiền điện sau thuế:</span>
                <span style={UIHelper.buildFontStyle('600', '16px', AppColors.primary)}>
                    {' '} {Utils.formatNumber(Math.floor(reconciliationDetail?.metadata?.totalEnergyCost) ?? 0)} VNĐ
                </span>
            </div>
            {/* Body */}
            <div className="pt-4">
                <KTTable
                    columns={columns}
                    items={stationEnergyList}
                    selectableRows={false}
                    striped={false}
                    compactGrid={false}
                    onClickedRow={() => { }}
                    onSelectedRowsChanged={(rows) => { }}
                    onDoubleClickedRow={(rows) => { }}
                    noDataComponent={
                        <EmptyView
                            icon={imgEmptyNoData}
                            title="Không có dữ liệu để hiển thị"
                            text={
                                <div className="text-center">
                                    <span>
                                        Hiện không có dữ liệu để hiển thị. Vui lòng kiểm tra kết nối mạng hoặc tải lại trang.
                                    </span>
                                </div>
                            }
                        />
                    }
                />
            </div>

            {/* Phân trang */}
            {!_.isEmpty(stationEnergyList) && (
                <div className="mt-4">
                    <KTPagination
                        currentPage={filter.page + 1}
                        rowCount={stationEnergyTotal}
                        rowsPerPage={filter.limit}
                        currentItemInPageCount={stationEnergyList.length}
                        onChangePage={(newPage) => {
                            const iNewPage = parseInt(newPage);
                            handleFilterChanged({
                                page: iNewPage - 1,
                            });
                        }}
                        onChangeRowsPerPage={(newRowsPerPage) => {
                            const iNewRowsPerPage = parseInt(newRowsPerPage);
                            handleFilterChanged({
                                limit: iNewRowsPerPage,
                                page: 0,
                            });
                        }}
                        loading={false}
                        color={KTPaginationColor.success}
                        appendText="trạm sạc"
                    />
                </div>
            )}
        </div>
    );
}

export default StationEnergyView;
